/**
 * Admin -- Settings/API & Webhooks
 *
 * Three sub-tabs:
 *   1. API Keys (W1b-04)    -- WIRED to /admin/settings/api-keys
 *   2. Webhooks (W1b-05)    -- still mock; Agent Z is wiring this in a
 *                              parallel worktree
 *   3. API Metadata (W1b-06) -- WIRED to /admin/settings/general
 *                              (CORS allowlist namespace) + OpenAPI export
 *                              served by /api/docs/json
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Key, Webhook, FileJson, Plus, Copy, RotateCw, Trash2, Pause, Play,
  MoreHorizontal, CheckCircle2, XCircle, AlertCircle, Clock,
  Send, Eye, X as XIcon, Download, Activity, Code, Shield,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import {
  SectionCard, SettingField, StatusDot,
} from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';
import { useAdminAuditMutation, evaluateGates } from '@/hooks/useAdminAuditMutation';
// Issue #1113 — Webhooks sub-tab is now fully wired against the live
// backend (W1b-05). The legacy mock-data imports (`WEBHOOKS`,
// `WEBHOOK_EVENT_CATALOG`, `WEBHOOK_DELIVERIES` + their helper types)
// have been dropped; Keys + Metadata + Webhooks all consume real APIs.
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useRotateWebhookSecret,
  useTestFireWebhook,
  useWebhookEventCatalog,
  useWebhookDeliveries,
  type WebhookSummary,
  type WebhookCreateBody,
  type WebhookUpdateBody,
  type WebhookDeliveryStatus,
  type WebhookDelivery as LiveWebhookDelivery,
} from '@/lib/use-webhooks';

export const Route = createFileRoute('/admin/settings/api')({
  component: ApiPage,
});

// ─── Backend types (W1b-04) ────────────────────────────────────────────────

type ScopeKey =
  | 'chat:read' | 'chat:write'
  | 'docs:read' | 'docs:write'
  | 'agents:read' | 'agents:invoke' | 'agents:manage'
  | 'analytics:read'
  | 'admin:read' | 'admin:write';

type KeyStatus =
  | 'active' | 'rotating' | 'retired' | 'disabled' | 'expired';

interface KeySummary {
  id: string;
  name: string;
  prefix: string;
  scopes: ScopeKey[];
  status: KeyStatus;
  ipZoneId: string | null;
  rateLimitOverridePerMinute: number | null;
  autoRotateSchedule: 'never' | '30d' | '90d' | '180d' | '365d';
  verificationOnlyUntil: string | null;
  lastUsedAt: string | null;
  lastUsedFromIp: string | null;
  calls24h: number;
  calls7d: number;
  calls30d: number;
  expiresAt: string | null;
  rotatedAt: string | null;
  createdAt: string;
}

interface ScopeMeta {
  key: ScopeKey;
  label: string;
  description: string;
  risk: 'low' | 'medium' | 'high';
}

const HIGH_RISK_SCOPES = new Set<ScopeKey>([
  'docs:write', 'agents:manage', 'admin:write',
]);

function ApiPage() {
  const [tab, setTab] = useState<'keys' | 'webhooks' | 'metadata'>('keys');

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">API & Webhooks</h1>
        <p className="text-xs text-muted-foreground mt-1">
          API keys for external integrations, webhook endpoints for event notifications.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="h-9">
          <TabsTrigger value="keys" className="text-xs gap-1.5">
            <Key className="h-3.5 w-3.5" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="text-xs gap-1.5">
            <Webhook className="h-3.5 w-3.5" />
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="metadata" className="text-xs gap-1.5">
            <FileJson className="h-3.5 w-3.5" />
            API Metadata
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-4"><ApiKeysSection /></TabsContent>
        <TabsContent value="webhooks" className="mt-4"><WebhooksSection /></TabsContent>
        <TabsContent value="metadata" className="mt-4"><MetadataSection /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  W1b-04 -- API Keys section (WIRED)
// ═══════════════════════════════════════════════════════════════════════════

function ApiKeysSection() {
  const [keys, setKeys] = useState<KeySummary[]>([]);
  const [scopeCatalogue, setScopeCatalogue] = useState<ScopeMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<{ value: string; name: string } | null>(null);
  const [rotateKey, setRotateKey] = useState<KeySummary | null>(null);
  const [deleteKey, setDeleteKey] = useState<KeySummary | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [list, cat] = await Promise.all([
        apiClient.get<{ keys: KeySummary[]; total: number }>(
          '/admin/settings/api-keys/'
        ),
        apiClient.get<{ scopes: ScopeMeta[] }>(
          '/admin/settings/api-keys/scope-catalogue'
        ),
      ]);
      setKeys(list.keys);
      setScopeCatalogue(cat.scopes);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load API keys';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function setStatus(
    id: string,
    action: 'disable' | 'enable',
    reason: string
  ) {
    try {
      await apiClient.post(`/admin/settings/api-keys/${id}/${action}`, {
        reason,
      });
      toast.success(`Key ${action}d`);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : `${action} failed`;
      toast.error(msg);
    }
  }

  return (
    <div className="space-y-4">
      <SectionCard
        title="API Keys"
        description="API keys authenticate external integrations. Keys are hashed at rest and shown only once at creation."
        icon={Key}
        actions={
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={loading || scopeCatalogue.length === 0}
            title="Create a new API key"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New key
          </Button>
        }
      >
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading API keys...
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-md">
            <Key className="h-6 w-6 text-muted-foreground/50 mb-2" />
            <div className="text-xs font-medium">No API keys</div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Create a key to allow external systems to call ArgusAI.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <div className="grid grid-cols-[minmax(200px,1.5fr)_120px_minmax(180px,1fr)_100px_110px_100px_72px] gap-2 px-3 py-2 bg-muted/50 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b">
              <span>Name</span>
              <span>Prefix</span>
              <span>Scopes</span>
              <span>Last used</span>
              <span>Calls (24h)</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
            <div className="divide-y">
              {keys.map((k) => (
                <ApiKeyRow
                  key={k.id}
                  apiKey={k}
                  onRotate={() => setRotateKey(k)}
                  onDelete={() => setDeleteKey(k)}
                  onDisable={() =>
                    setStatus(k.id, 'disable', 'Disabled from admin UI')
                  }
                  onEnable={() =>
                    setStatus(k.id, 'enable', 'Re-enabled from admin UI')
                  }
                />
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        scopeCatalogue={scopeCatalogue}
        onCreated={async (plaintext, name) => {
          setCreateOpen(false);
          setRevealKey({ value: plaintext, name });
          await refresh();
        }}
      />

      <RevealKeyDialog
        open={!!revealKey}
        onOpenChange={() => setRevealKey(null)}
        keyValue={revealKey?.value ?? ''}
        keyName={revealKey?.name ?? ''}
      />

      {rotateKey && (
        <RotateKeyDialog
          apiKey={rotateKey}
          onClose={() => setRotateKey(null)}
          onRotated={async (plaintext) => {
            const oldName = rotateKey.name;
            setRotateKey(null);
            setRevealKey({ value: plaintext, name: oldName });
            await refresh();
          }}
        />
      )}

      {deleteKey && (
        <DeleteKeyDialog
          apiKey={deleteKey}
          onClose={() => setDeleteKey(null)}
          onDeleted={async () => {
            setDeleteKey(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function ApiKeyRow({
  apiKey,
  onRotate,
  onDelete,
  onDisable,
  onEnable,
}: {
  apiKey: KeySummary;
  onRotate: () => void;
  onDelete: () => void;
  onDisable: () => void;
  onEnable: () => void;
}) {
  const statusStyles: Record<
    KeyStatus,
    { color: string; bg: string; border: string; label: string }
  > = {
    active: {
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      label: 'Active',
    },
    rotating: {
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      label: 'Rotating',
    },
    retired: {
      color: 'text-muted-foreground',
      bg: 'bg-muted',
      border: 'border-border',
      label: 'Retired',
    },
    disabled: {
      color: 'text-muted-foreground',
      bg: 'bg-muted',
      border: 'border-border',
      label: 'Disabled',
    },
    expired: {
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      label: 'Expired',
    },
  };
  const s = statusStyles[apiKey.status];

  const expiresInDays = apiKey.expiresAt
    ? Math.ceil(
        (new Date(apiKey.expiresAt).getTime() - Date.now()) / 86_400_000
      )
    : null;
  const expiringSoon =
    apiKey.status === 'active' &&
    expiresInDays !== null &&
    expiresInDays <= 30;

  return (
    <div className="grid grid-cols-[minmax(200px,1.5fr)_120px_minmax(180px,1fr)_100px_110px_100px_72px] gap-2 px-3 py-2.5 items-center text-xs hover:bg-muted/20">
      <div className="min-w-0">
        <div className="font-medium truncate">{apiKey.name}</div>
        <div className="text-[10px] text-muted-foreground">
          Created {new Date(apiKey.createdAt).toLocaleDateString()}
        </div>
      </div>

      <code className="text-[11px] font-mono bg-muted/50 px-1.5 py-0.5 rounded w-fit">
        {apiKey.prefix}...
      </code>

      <div className="flex flex-wrap gap-1">
        {apiKey.scopes.slice(0, 2).map((sc) => (
          <Badge
            key={sc}
            variant="outline"
            className="text-[9px] px-1 h-4 font-mono"
          >
            {sc}
          </Badge>
        ))}
        {apiKey.scopes.length > 2 && (
          <Badge variant="outline" className="text-[9px] px-1 h-4">
            +{apiKey.scopes.length - 2}
          </Badge>
        )}
      </div>

      <div className="text-[11px] text-muted-foreground tabular-nums">
        {apiKey.lastUsedAt ? relativeTime(apiKey.lastUsedAt) : 'Never'}
      </div>

      <div className="tabular-nums font-medium">
        {apiKey.calls24h.toLocaleString()}
      </div>

      <div className="flex items-center gap-1">
        <Badge
          variant="outline"
          className={cn('text-[10px] gap-1', s.bg, s.color, s.border)}
        >
          {s.label}
        </Badge>
        {expiringSoon && expiresInDays !== null && (
          <span title={`Expires in ${expiresInDays}d`} className="inline-flex">
            <Clock className="h-3 w-3 text-amber-500" />
          </span>
        )}
      </div>

      <div className="flex items-center justify-end gap-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="More actions"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-xs"
              onSelect={() => {
                navigator.clipboard.writeText(apiKey.id);
                toast.success('Key ID copied');
              }}
            >
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy key ID
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs" onSelect={onRotate}>
              <RotateCw className="h-3.5 w-3.5 mr-2" /> Rotate key
            </DropdownMenuItem>
            {apiKey.status === 'active' && (
              <DropdownMenuItem className="text-xs" onSelect={onDisable}>
                <Pause className="h-3.5 w-3.5 mr-2" /> Disable
              </DropdownMenuItem>
            )}
            {apiKey.status === 'disabled' && (
              <DropdownMenuItem className="text-xs" onSelect={onEnable}>
                <Play className="h-3.5 w-3.5 mr-2" /> Re-enable
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs text-red-600 dark:text-red-400"
              onSelect={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete key
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function CreateApiKeyDialog({
  open,
  onOpenChange,
  scopeCatalogue,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  scopeCatalogue: ScopeMeta[];
  onCreated: (plaintext: string, name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ScopeKey[]>(['chat:read']);
  const [expiresInDays, setExpiresInDays] = useState(365);
  const [autoRotate, setAutoRotate] =
    useState<'never' | '30d' | '90d' | '180d' | '365d'>('never');
  const [complianceAck, setComplianceAck] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const hasHighRisk = scopes.some((s) => HIGH_RISK_SCOPES.has(s));
  const canCreate =
    name.trim().length >= 3 &&
    scopes.length > 0 &&
    (!hasHighRisk || complianceAck);

  const toggleScope = (s: ScopeKey) => {
    setScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  async function submit() {
    if (!canCreate) return;
    try {
      setSaving(true);
      const r = await apiClient.post<{
        id: string;
        name: string;
        prefix: string;
        value: string;
        scopes: ScopeKey[];
        status: 'active';
        createdAt: string;
      }>('/admin/settings/api-keys/', {
        name: name.trim(),
        scopes,
        autoRotateSchedule: autoRotate,
        expiresInDays,
        complianceOfficerAcknowledged: complianceAck,
        reason: reason.trim() || undefined,
      });
      toast.success(`API key "${r.name}" created`);
      // Reset form
      setName('');
      setScopes(['chat:read']);
      setComplianceAck(false);
      setReason('');
      await onCreated(r.value, r.name);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            New API key
          </DialogTitle>
          <DialogDescription>
            Generate a new API key. The full key is shown once at the end of
            this dialog -- the server only stores a hash.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-4">
          <SettingField
            label="Name"
            required
            description="For internal identification only."
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CMMS Integration"
              className="h-9 text-xs"
            />
          </SettingField>

          <SettingField
            label="Scopes"
            required
            description="Grant only the permissions this integration needs."
          >
            <div className="space-y-1.5">
              {scopeCatalogue.map((meta) => {
                const s = meta.key;
                const riskColor =
                  meta.risk === 'high'
                    ? 'text-red-600 dark:text-red-400'
                    : meta.risk === 'medium'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground';
                return (
                  <label
                    key={s}
                    className={cn(
                      'flex items-start gap-2 rounded-md border p-2 cursor-pointer',
                      scopes.includes(s)
                        ? 'bg-primary/5 border-primary/30'
                        : 'hover:bg-muted/50'
                    )}
                  >
                    <Checkbox
                      checked={scopes.includes(s)}
                      onCheckedChange={() => toggleScope(s)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <code className="text-[10px] font-mono">{s}</code>
                        <Badge
                          variant="outline"
                          className={cn('text-[9px] capitalize', riskColor)}
                        >
                          {meta.risk}
                        </Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {meta.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </SettingField>

          <div className="grid grid-cols-2 gap-3">
            <SettingField label="Expires after">
              <Select
                value={String(expiresInDays)}
                onValueChange={(v) => setExpiresInDays(Number(v))}
              >
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30"  className="text-xs">30 days</SelectItem>
                  <SelectItem value="90"  className="text-xs">90 days</SelectItem>
                  <SelectItem value="180" className="text-xs">180 days</SelectItem>
                  <SelectItem value="365" className="text-xs">365 days (max)</SelectItem>
                </SelectContent>
              </Select>
            </SettingField>

            <SettingField
              label="Auto-rotate"
              description="Creates a new key before expiry."
            >
              <Select
                value={autoRotate}
                onValueChange={(v) =>
                  setAutoRotate(v as typeof autoRotate)
                }
              >
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="never" className="text-xs">Never</SelectItem>
                  <SelectItem value="30d"   className="text-xs">Every 30 days</SelectItem>
                  <SelectItem value="90d"   className="text-xs">Every 90 days</SelectItem>
                  <SelectItem value="180d"  className="text-xs">Every 180 days</SelectItem>
                  <SelectItem value="365d"  className="text-xs">Every 365 days</SelectItem>
                </SelectContent>
              </Select>
            </SettingField>
          </div>

          {hasHighRisk && (
            <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4" />
                High-risk scopes selected
              </div>
              <p className="text-[11px] text-red-700/80 dark:text-red-300/80">
                docs:write, agents:manage, and admin:write require a
                compliance-officer acknowledgment. The audit chain records
                this acknowledgment with your reason.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={complianceAck}
                  onCheckedChange={(v) => setComplianceAck(v === true)}
                  className="mt-0.5"
                />
                <span className="text-[11px]">
                  I have compliance-officer approval for this high-risk key.
                </span>
              </label>
            </div>
          )}

          <SettingField
            label="Reason (optional)"
            description="Recorded in audit chain for forensics."
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. SAP S/4 connector key for prod"
            />
          </SettingField>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canCreate || saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            <Plus className="h-4 w-4 mr-1.5" /> Generate key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealKeyDialog({
  open, onOpenChange, keyValue, keyName,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  keyValue: string;
  keyName: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            API key for &quot;{keyName || 'new key'}&quot;
          </DialogTitle>
          <DialogDescription>
            Copy this key now. It will never be shown again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border-2 border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold">This key cannot be retrieved later.</p>
              <p className="mt-0.5">
                Store it in a secrets manager. If you lose it, rotate the
                key and create a new one.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">API key</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono bg-muted px-3 py-2 rounded break-all">
                {keyValue}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                title="Copy key to clipboard"
                onClick={() => {
                  navigator.clipboard.writeText(keyValue);
                  setCopied(true);
                  toast.success('Key copied');
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            I have copied the key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RotateKeyDialog({
  apiKey, onClose, onRotated,
}: {
  apiKey: KeySummary;
  onClose: () => void;
  onRotated: (plaintext: string) => Promise<void>;
}) {
  const [overlapHours, setOverlapHours] =
    useState<0 | 1 | 6 | 24 | 72 | 168>(24);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (reason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    try {
      setSaving(true);
      const r = await apiClient.post<{
        id: string;
        value: string;
        prefix: string;
        previousId: string;
        verificationOnlyUntil: string | null;
      }>(`/admin/settings/api-keys/${apiKey.id}/rotate`, {
        overlapHours,
        reason: reason.trim(),
      });
      toast.success(
        overlapHours === 0
          ? `Key "${apiKey.name}" rotated - old key revoked immediately`
          : `Key "${apiKey.name}" rotated - old key valid for ${overlapHours}h`
      );
      await onRotated(r.value);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rotate failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCw className="h-5 w-5 text-primary" />
            Rotate API key
          </DialogTitle>
          <DialogDescription>
            Generate a new key for <strong>{apiKey.name}</strong>. Both old
            and new keys will be valid during the overlap period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField
            label="Overlap period"
            description="How long the old key remains valid after the new one is issued."
          >
            <Select
              value={String(overlapHours)}
              onValueChange={(v) =>
                setOverlapHours(Number(v) as typeof overlapHours)
              }
            >
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0"   className="text-xs">No overlap (old key revoked immediately)</SelectItem>
                <SelectItem value="1"   className="text-xs">1 hour</SelectItem>
                <SelectItem value="6"   className="text-xs">6 hours</SelectItem>
                <SelectItem value="24"  className="text-xs">24 hours (recommended)</SelectItem>
                <SelectItem value="72"  className="text-xs">72 hours</SelectItem>
                <SelectItem value="168" className="text-xs">7 days</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          <div className="space-y-1.5">
            <Label htmlFor="rotate-reason">Reason</Label>
            <Textarea
              id="rotate-reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Quarterly rotation"
            />
          </div>

          <div className="rounded-md border bg-muted/20 p-3 text-xs">
            <p className="font-semibold mb-1">During rotation:</p>
            <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
              <li>New key generated and shown once</li>
              <li>
                Old key (<code>{apiKey.prefix}...</code>) accepts requests for{' '}
                {overlapHours}h
              </li>
              <li>After overlap, old key is permanently revoked</li>
              <li>Update your integration before overlap expires</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            <RotateCw className="h-4 w-4 mr-1.5" /> Rotate now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * DeleteKeyDialog
 *
 * W1b-37 reference migration: this dialog is the canonical example of
 * how to consume {@link useAdminAuditMutation} for a destructive admin
 * action.  See `docs/waves/wave-1b/W1b-35-37-38-status.md` for the full
 * migration playbook for the remaining ~34 admin dialogs.
 */
function DeleteKeyDialog({
  apiKey, onClose, onDeleted,
}: {
  apiKey: KeySummary;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const audit = useAdminAuditMutation<void, void>({
    mutationFn: async (_vars, auditMeta) => {
      await apiClient.request(`/admin/settings/api-keys/${apiKey.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmPhrase: apiKey.prefix,
          reason: auditMeta.reason ?? 'deleted via admin UI',
        }),
      });
    },
    category: 'data_modification',
    action: 'admin.api_key.deleted',
    requireReason: true,
    requireTypedConfirm: { phrase: apiKey.prefix },
    successMessage: 'API key deleted',
  });

  async function submit() {
    try {
      await audit.submit(undefined as void);
      // Successful submit: refresh parent + close.
      if (audit.state === 'submitting' || audit.state === 'success') {
        await onDeleted();
      }
    } catch {
      /* toast already surfaced by the hook */
    }
  }

  // Mirror the FSM gate evaluation so the destructive button is
  // disabled-until-valid (typed prefix + reason), matching the
  // TypedConfirmDialog pattern. The on-click gate in submit() still
  // enforces this; this just makes the button state honest.
  const gatesGreen =
    evaluateGates(
      {
        reason: audit.reason,
        typedConfirmInput: audit.typedConfirmInput,
        secondApproverInput: audit.secondApproverInput,
      },
      {
        requireReason: true,
        requireTypedConfirm: { phrase: apiKey.prefix },
        require2Person: false,
      },
    ) === 'submitting';

  // Friendly inline error when a gate is unmet (the hook captures the
  // unmet gate in `audit.state`; toasts only fire on submit-failure).
  const gateMessage =
    audit.state === 'awaiting_reason'
      ? 'Reason must be at least 3 characters'
      : audit.state === 'awaiting_typed_confirm'
        ? `Type "${apiKey.prefix}" to confirm`
        : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete API key</DialogTitle>
          <DialogDescription>
            Delete <strong>{apiKey.name}</strong>. This breaks any
            integration using this key immediately. Type the prefix to
            confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border-2 border-red-500/40 bg-red-500/5 p-3 text-xs text-red-700 dark:text-red-300">
            <p className="font-semibold">Consequences</p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>All API calls using this key fail with 401 immediately</li>
              <li>No grace period - disable first if you need one</li>
              <li>Audit chain records the deletion with your reason</li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="del-confirm">
              Type <code className="font-mono">{apiKey.prefix}</code> to confirm
            </Label>
            <Input
              id="del-confirm"
              value={audit.typedConfirmInput}
              onChange={(e) => audit.setTypedConfirmInput(e.target.value)}
              className="h-9 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="del-reason">Reason</Label>
            <Textarea
              id="del-reason"
              rows={2}
              value={audit.reason}
              onChange={(e) => audit.setReason(e.target.value)}
              placeholder="e.g. Compromised, no longer used"
            />
          </div>
          {gateMessage && (
            <p className="text-xs text-red-600 dark:text-red-400">{gateMessage}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={audit.isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={audit.isLoading || !gatesGreen}
          >
            {audit.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Webhooks section ──────────────────────────────────────────────────────
//
// W1b-05 — fully wired to /api/v1/admin/settings/webhooks via the hooks
// in `lib/use-webhooks.ts`. Create / list / detail / PATCH / DELETE /
// rotate-secret / test-fire / per-webhook delivery log all hit the live
// backend.
//
// Issue #1113 — the legacy cross-webhook delivery-log summary
// (`WebhookDeliveryLogSheet`) was backed by mock data, and a misleading
// amber "still mock" warning banner stayed visible above a fully-live
// table. Both have been removed. The per-webhook live delivery sheet
// (opened from a row click) remains as the canonical delivery-history
// view; a true cross-webhook aggregator can land later if needed.

function WebhooksSection() {
  const { data, isLoading, error } = useWebhooks();
  const createMut = useCreateWebhook();
  const updateMut = useUpdateWebhook();
  const deleteMut = useDeleteWebhook();
  const rotateMut = useRotateWebhookSecret();
  const testFireMut = useTestFireWebhook();

  const [perWebhookDeliveriesId, setPerWebhookDeliveriesId] =
    useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editWebhook, setEditWebhook] = useState<WebhookSummary | null>(null);
  const [deleteWebhook, setDeleteWebhook] = useState<WebhookSummary | null>(
    null
  );
  const [rotateWebhook, setRotateWebhook] = useState<WebhookSummary | null>(
    null
  );
  const [revealedSecret, setRevealedSecret] = useState<{
    name: string;
    secret: string;
    rotatedAt: string;
  } | null>(null);
  const [testFireWebhook, setTestFireWebhook] =
    useState<WebhookSummary | null>(null);

  const liveWebhooks = data?.webhooks ?? [];

  return (
    <div className="space-y-4">
      <SectionCard
        title="Webhooks"
        description="HTTP endpoints that receive event notifications. Every request is HMAC-signed."
        icon={Webhook}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setCreateOpen(true)} title="Add webhook">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New webhook
            </Button>
          </div>
        }
      >
        {isLoading ? (
          <div className="text-[11px] text-muted-foreground py-6 text-center">
            Loading webhooks…
          </div>
        ) : error ? (
          <div className="text-[11px] text-red-600 dark:text-red-400 py-6 text-center">
            Failed to load webhooks: {error.message}
          </div>
        ) : liveWebhooks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-md">
            <Webhook className="h-6 w-6 text-muted-foreground/50 mb-2" />
            <div className="text-xs font-medium">No webhooks configured</div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Add a webhook to receive event notifications via HTTP.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {liveWebhooks.map((w) => (
              <WebhookRowLive
                key={w.id}
                webhook={w}
                onEdit={() => setEditWebhook(w)}
                onTogglePause={() => {
                  updateMut.mutate(
                    {
                      id: w.id,
                      body: {
                        enabled: !w.enabled,
                        reason: w.enabled
                          ? 'paused via admin UI'
                          : 'enabled via admin UI',
                      },
                    },
                    {
                      onSuccess: () => {
                        toast.success(
                          w.enabled ? 'Webhook paused' : 'Webhook enabled'
                        );
                      },
                      onError: (err) => toast.error(err.message),
                    }
                  );
                }}
                onRotateSecret={() => setRotateWebhook(w)}
                onTest={() => setTestFireWebhook(w)}
                onDelete={() => setDeleteWebhook(w)}
                onViewDeliveries={() => setPerWebhookDeliveriesId(w.id)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Event catalog summary — live from API with fallback to mock */}
      <Card>
        <CardContent className="p-4">
          <EventCatalogLive />
        </CardContent>
      </Card>

      {/* Per-webhook live delivery log (issue #1113: cross-webhook
          aggregator was mock-backed and has been removed). */}
      {perWebhookDeliveriesId && (
        <WebhookDeliveryLogLiveSheet
          webhookId={perWebhookDeliveriesId}
          open={!!perWebhookDeliveriesId}
          onOpenChange={(o) => !o && setPerWebhookDeliveriesId(null)}
        />
      )}

      {/* Create dialog */}
      {createOpen && (
        <WebhookFormDialog
          mode="create"
          open
          onOpenChange={setCreateOpen}
          onSubmit={(body) => {
            createMut.mutate(body as WebhookCreateBody, {
              onSuccess: (resp) => {
                toast.success('Webhook created');
                setCreateOpen(false);
                setRevealedSecret({
                  name: resp.name,
                  secret: resp.signingSecret,
                  rotatedAt: resp.createdAt,
                });
              },
              onError: (err) => toast.error(err.message),
            });
          }}
          submitting={createMut.isPending}
        />
      )}

      {/* Edit dialog */}
      {editWebhook && (
        <WebhookFormDialog
          mode="edit"
          open
          initial={editWebhook}
          onOpenChange={(v) => !v && setEditWebhook(null)}
          onSubmit={(body) => {
            updateMut.mutate(
              { id: editWebhook.id, body: body as WebhookUpdateBody },
              {
                onSuccess: () => {
                  toast.success('Webhook updated');
                  setEditWebhook(null);
                },
                onError: (err) => toast.error(err.message),
              }
            );
          }}
          submitting={updateMut.isPending}
        />
      )}

      {/* Rotate secret dialog */}
      {rotateWebhook && (
        <RotateSecretDialog
          open
          webhookName={rotateWebhook.name}
          onOpenChange={(v) => !v && setRotateWebhook(null)}
          onConfirm={(reason) => {
            rotateMut.mutate(
              { id: rotateWebhook.id, reason },
              {
                onSuccess: (resp) => {
                  toast.success(
                    'Signing secret rotated — old secret valid for 7 more days'
                  );
                  setRotateWebhook(null);
                  setRevealedSecret({
                    name: rotateWebhook.name,
                    secret: resp.signingSecret,
                    rotatedAt: resp.signingSecretRotatedAt,
                  });
                },
                onError: (err) => toast.error(err.message),
              }
            );
          }}
          submitting={rotateMut.isPending}
        />
      )}

      {/* Reveal-once secret dialog (after create or rotate) */}
      {revealedSecret && (
        <RevealOnceSecretDialog
          open
          name={revealedSecret.name}
          secret={revealedSecret.secret}
          rotatedAt={revealedSecret.rotatedAt}
          onOpenChange={(v) => !v && setRevealedSecret(null)}
        />
      )}

      {/* Test-fire dialog */}
      {testFireWebhook && (
        <TestFireDialog
          open
          webhookName={testFireWebhook.name}
          onOpenChange={(v) => !v && setTestFireWebhook(null)}
          onConfirm={(eventType) => {
            testFireMut.mutate(
              { id: testFireWebhook.id, eventType },
              {
                onSuccess: (resp) => {
                  toast.success(
                    `Test fired — status: ${resp.status}${
                      resp.statusCode ? ` (HTTP ${resp.statusCode})` : ''
                    }`
                  );
                  setTestFireWebhook(null);
                },
                onError: (err) => toast.error(err.message),
              }
            );
          }}
          submitting={testFireMut.isPending}
        />
      )}

      {/* Delete dialog */}
      {deleteWebhook && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleteWebhook(null)}
          title="Delete webhook"
          description={`Delete ${deleteWebhook.name}? Events will stop being delivered to this endpoint immediately.`}
          consequences={[
            `${deleteWebhook.events.length} event subscriptions will be lost`,
            'In-flight deliveries are abandoned',
            'Past delivery records are retained for audit',
          ]}
          confirmPhrase={deleteWebhook.name}
          confirmLabel="Delete webhook"
          variant="danger"
          requireReason
          onConfirm={(payload) => {
            deleteMut.mutate(
              {
                id: deleteWebhook.id,
                confirmPhrase: deleteWebhook.name,
                reason: payload.reason ?? 'deleted via admin UI',
              },
              {
                onSuccess: () => {
                  toast.success('Webhook deleted');
                  setDeleteWebhook(null);
                },
                onError: (err) => toast.error(err.message),
              }
            );
          }}
        />
      )}
    </div>
  );
}

// ─── Live row + helpers ─────────────────────────────────────────────────────

function WebhookRowLive({
  webhook,
  onEdit,
  onTogglePause,
  onRotateSecret,
  onTest,
  onDelete,
  onViewDeliveries,
}: {
  webhook: WebhookSummary;
  onEdit: () => void;
  onTogglePause: () => void;
  onRotateSecret: () => void;
  onTest: () => void;
  onDelete: () => void;
  onViewDeliveries: () => void;
}) {
  const total24h = webhook.successCount24h + webhook.failureCount24h;
  const failRate =
    total24h > 0 ? webhook.failureCount24h / total24h : 0;
  const status = !webhook.enabled
    ? 'disabled'
    : failRate > 0.1
      ? 'failed'
      : failRate > 0
        ? 'degraded'
        : 'healthy';

  return (
    <div className="rounded-lg border p-3 hover:bg-muted/20 transition-colors">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'h-9 w-9 rounded-md flex items-center justify-center shrink-0',
            webhook.enabled ? 'bg-primary/10' : 'bg-muted'
          )}
        >
          <Webhook
            className={cn(
              'h-4 w-4',
              webhook.enabled ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-semibold">{webhook.name}</span>
            <Badge
              variant="outline"
              className={cn(
                'text-[9px] gap-1',
                status === 'healthy' &&
                  'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
                status === 'degraded' &&
                  'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
                status === 'failed' &&
                  'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
                status === 'disabled' &&
                  'bg-muted text-muted-foreground border-border'
              )}
            >
              <StatusDot
                status={
                  status === 'healthy'
                    ? 'healthy'
                    : status === 'disabled'
                      ? 'disabled'
                      : status === 'degraded'
                        ? 'degraded'
                        : 'failed'
                }
              />
              {status}
            </Badge>
            {webhook.mtlsClientCertId && (
              <Badge variant="outline" className="text-[9px] gap-1">
                <Shield className="h-2.5 w-2.5" /> mTLS
              </Badge>
            )}
            <Badge variant="outline" className="text-[9px] tabular-nums">
              {webhook.events.length} events
            </Badge>
          </div>
          <button
            type="button"
            className="text-left w-full"
            onClick={onViewDeliveries}
            title="View delivery log for this webhook"
          >
            <code className="text-[11px] font-mono text-muted-foreground truncate block hover:text-foreground">
              {webhook.url}
            </code>
          </button>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground flex-wrap">
            <span className="tabular-nums">
              {webhook.successCount24h} success ·{' '}
              {webhook.failureCount24h} failure (24h)
            </span>
            <span>·</span>
            <span>
              Last{' '}
              {webhook.lastDeliveryAt
                ? relativeTime(webhook.lastDeliveryAt)
                : 'never'}
            </span>
            <span>·</span>
            <span className="font-mono">{webhook.signingSecretPrefix}…</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={onTest}
            title="Send a test event to this endpoint"
          >
            <Send className="h-3 w-3 mr-1" /> Test
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="More actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-xs" onSelect={onEdit}>
                <Eye className="h-3.5 w-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs"
                onSelect={onViewDeliveries}
              >
                <Activity className="h-3.5 w-3.5 mr-2" /> Delivery log
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs"
                onSelect={onRotateSecret}
              >
                <RotateCw className="h-3.5 w-3.5 mr-2" /> Rotate signing secret
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs"
                onSelect={() => {
                  navigator.clipboard.writeText(webhook.id);
                  toast.success('Webhook ID copied');
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy ID
              </DropdownMenuItem>
              {webhook.enabled ? (
                <DropdownMenuItem
                  className="text-xs"
                  onSelect={onTogglePause}
                >
                  <Pause className="h-3.5 w-3.5 mr-2" /> Pause
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="text-xs"
                  onSelect={onTogglePause}
                >
                  <Play className="h-3.5 w-3.5 mr-2" /> Enable
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-xs text-red-600 dark:text-red-400"
                onSelect={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

function EventCatalogLive() {
  const { data, isLoading } = useWebhookEventCatalog();
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const allEvents = useMemo(() => {
    if (!data) return [];
    return data.categories.flatMap((c) =>
      c.events.map((e) => ({ ...e, category: c.category }))
    );
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allEvents;
    return allEvents.filter(
      (e) =>
        e.key.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q),
    );
  }, [allEvents, search]);

  const grouped = useMemo(() => {
    const g: Record<string, typeof filtered> = {};
    for (const e of filtered) {
      if (!g[e.category]) g[e.category] = [];
      g[e.category].push(e);
    }
    return g;
  }, [filtered]);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Event catalog
        </h3>
        <Badge variant="outline" className="text-[10px] tabular-nums">
          {data?.totalEvents ?? 0} events
        </Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        All events that can be subscribed to. Search across keys and
        descriptions.
      </p>
      <Input
        placeholder="Search events…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-xs mb-3"
      />
      {isLoading ? (
        <div className="text-[11px] text-muted-foreground py-3">
          Loading event catalog…
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {Object.entries(grouped).map(([cat, events]) => (
            <div key={cat}>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                {cat}
              </p>
              <div className="space-y-1">
                {events.map((e) => (
                  <div
                    key={e.key}
                    className="rounded-md border p-2 hover:bg-muted/30 cursor-pointer"
                    onClick={() =>
                      setExpandedKey(expandedKey === e.key ? null : e.key)
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-[11px] font-mono">{e.key}</code>
                      <span className="text-[10px] text-muted-foreground">
                        {expandedKey === e.key ? 'Hide' : 'Details'}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {e.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Live per-webhook delivery log ─────────────────────────────────────────

function WebhookDeliveryLogLiveSheet({
  webhookId,
  open,
  onOpenChange,
}: {
  webhookId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<
    'all' | WebhookDeliveryStatus
  >('all');
  const [eventFilter, setEventFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const { data, isLoading } = useWebhookDeliveries(webhookId, {
    status: statusFilter === 'all' ? undefined : statusFilter,
    event: eventFilter.trim() || undefined,
    limit,
    offset,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl w-full overflow-hidden flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <SheetTitle>Webhook Delivery Log</SheetTitle>
          <SheetDescription>
            Most recent dispatches for this endpoint. HMAC signature
            verification is performed by the receiver.
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b flex items-center gap-2 flex-wrap">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as 'all' | WebhookDeliveryStatus);
              setOffset(0);
            }}
          >
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All statuses
              </SelectItem>
              <SelectItem value="queued" className="text-xs">
                Queued
              </SelectItem>
              <SelectItem value="success" className="text-xs">
                Success
              </SelectItem>
              <SelectItem value="failed" className="text-xs">
                Failed
              </SelectItem>
              <SelectItem value="retrying" className="text-xs">
                Retrying
              </SelectItem>
              <SelectItem value="dead_letter" className="text-xs">
                Dead letter
              </SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by event…"
            value={eventFilter}
            onChange={(e) => {
              setEventFilter(e.target.value);
              setOffset(0);
            }}
            className="h-8 text-xs w-48"
          />
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground tabular-nums">
            {data?.total ?? 0} total
          </span>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="p-6 text-[11px] text-muted-foreground text-center">
              Loading deliveries…
            </div>
          ) : !data || data.deliveries.length === 0 ? (
            <div className="p-6 text-[11px] text-muted-foreground text-center">
              No deliveries match the current filter.
            </div>
          ) : (
            <div className="divide-y">
              {data.deliveries.map((d) => (
                <DeliveryRowLive key={d.id} delivery={d} />
              ))}
            </div>
          )}
        </div>

        {data && data.total > limit && (
          <div className="p-3 border-t flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground tabular-nums">
              {offset + 1}–{Math.min(offset + limit, data.total)} of{' '}
              {data.total}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px]"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px]"
                disabled={offset + limit >= data.total}
                onClick={() => setOffset(offset + limit)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DeliveryRowLive({ delivery }: { delivery: LiveWebhookDelivery }) {
  const statusStyles: Record<
    WebhookDeliveryStatus,
    { icon: typeof CheckCircle2; color: string }
  > = {
    queued: { icon: Clock, color: 'text-blue-500' },
    success: { icon: CheckCircle2, color: 'text-emerald-500' },
    failed: { icon: XCircle, color: 'text-red-500' },
    retrying: { icon: Clock, color: 'text-amber-500' },
    dead_letter: {
      icon: AlertCircle,
      color: 'text-red-700 dark:text-red-400',
    },
  };
  const s = statusStyles[delivery.status];
  const Icon = s.icon;

  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30">
      <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', s.color)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-[11px] font-mono">{delivery.event}</code>
          {delivery.isTest && (
            <Badge variant="outline" className="text-[9px]">
              probe
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1 tabular-nums">
          <span>{relativeTime(delivery.dispatchedAt)}</span>
          {delivery.statusCode !== null && (
            <span>HTTP {delivery.statusCode}</span>
          )}
          {delivery.latencyMs !== null && (
            <span>{delivery.latencyMs}ms</span>
          )}
          {delivery.attemptCount > 1 && (
            <span>attempt {delivery.attemptCount}</span>
          )}
          <span className="capitalize">{delivery.status.replace('_', ' ')}</span>
        </div>
        {delivery.errorMessage && (
          <p className="text-[10px] text-red-600 dark:text-red-400 mt-1">
            {delivery.errorMessage}
          </p>
        )}
        {delivery.payloadSummary && (
          <pre className="text-[10px] font-mono bg-muted/50 p-2 rounded mt-2 overflow-x-auto whitespace-pre-wrap break-all">
            {delivery.payloadSummary}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── Webhook create/edit form dialog ─────────────────────────────────────

function WebhookFormDialog({
  mode,
  open,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: WebhookSummary;
  onSubmit: (body: WebhookCreateBody | WebhookUpdateBody) => void;
  submitting: boolean;
}) {
  const { data: catalog } = useWebhookEventCatalog();
  const [name, setName] = useState(initial?.name ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [events, setEvents] = useState<string[]>(initial?.events ?? []);
  const [reason, setReason] = useState('');

  function toggleEvent(key: string) {
    setEvents((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  function submit() {
    if (mode === 'create') {
      onSubmit({
        name,
        url,
        events,
        enabled,
        reason: reason || undefined,
      });
    } else {
      onSubmit({
        name: name !== initial?.name ? name : undefined,
        url: url !== initial?.url ? url : undefined,
        events,
        enabled: enabled !== initial?.enabled ? enabled : undefined,
        reason: reason || undefined,
      });
    }
  }

  const valid =
    name.trim().length > 0 &&
    url.trim().length > 0 &&
    /^https?:\/\//i.test(url) &&
    events.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'New webhook' : 'Edit webhook'}
          </DialogTitle>
          <DialogDescription>
            HMAC-SHA256 signed deliveries. Subscribe to one or more events.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="payments-receiver"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs">URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/argus-events"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Events</Label>
            <div className="border rounded-md p-2 max-h-60 overflow-y-auto space-y-2">
              {(catalog?.categories ?? []).map((cat) => (
                <div key={cat.category}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                    {cat.category}
                  </p>
                  <div className="space-y-1">
                    {cat.events.map((e) => (
                      <label
                        key={e.key}
                        className="flex items-start gap-2 text-[11px] cursor-pointer hover:bg-muted/30 rounded p-1"
                      >
                        <Checkbox
                          checked={events.includes(e.key)}
                          onCheckedChange={() => toggleEvent(e.key)}
                          className="mt-0.5"
                        />
                        <div>
                          <code className="font-mono">{e.key}</code>
                          <p className="text-muted-foreground">
                            {e.description}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {events.length} selected
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => setEnabled(v === true)}
            />
            Enabled
          </label>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="why this change…"
              className="h-8 text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || submitting}>
            {submitting
              ? 'Saving…'
              : mode === 'create'
                ? 'Create webhook'
                : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Rotate secret dialog ─────────────────────────────────────────────────

function RotateSecretDialog({
  open,
  onOpenChange,
  webhookName,
  onConfirm,
  submitting,
}: {
  open: boolean;
  webhookName: string;
  onOpenChange: (v: boolean) => void;
  onConfirm: (reason: string) => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate signing secret?</DialogTitle>
          <DialogDescription>
            A new HMAC-SHA256 signing secret will be generated for{' '}
            <span className="font-mono">{webhookName}</span>. The old secret
            stays valid for 7 days so you can update receivers without losing
            deliveries.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Reason</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="why rotate (logged to audit)"
            className="h-8 text-xs"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason || 'manual rotation via admin UI')}
            disabled={submitting || reason.trim().length < 3}
          >
            {submitting ? 'Rotating…' : 'Rotate secret'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reveal-once secret dialog ────────────────────────────────────────────

function RevealOnceSecretDialog({
  open,
  onOpenChange,
  name,
  secret,
  rotatedAt,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  name: string;
  secret: string;
  rotatedAt: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Signing secret</DialogTitle>
          <DialogDescription>
            Copy and store this secret in a safe place — it will not be shown
            again. Use it to verify HMAC-SHA256 signatures on incoming
            deliveries to <span className="font-mono">{name}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/30 p-3 break-all font-mono text-[11px]">
          {secret}
        </div>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          Rotated at {new Date(rotatedAt).toLocaleString()}
        </p>
        <DialogFooter>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(secret);
              toast.success('Secret copied');
            }}
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy secret
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            I've saved it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Test-fire dialog ─────────────────────────────────────────────────────

function TestFireDialog({
  open,
  onOpenChange,
  webhookName,
  onConfirm,
  submitting,
}: {
  open: boolean;
  webhookName: string;
  onOpenChange: (v: boolean) => void;
  onConfirm: (eventType: string) => void;
  submitting: boolean;
}) {
  const { data: catalog } = useWebhookEventCatalog();
  const [eventType, setEventType] = useState('auth.login.success');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send a test event</DialogTitle>
          <DialogDescription>
            Fires a synthetic event through{' '}
            <span className="font-mono">{webhookName}</span>. The receiver
            sees{' '}
            <code className="font-mono">X-ArgusAI-Probe: 1</code> and the
            delivery is recorded with{' '}
            <code className="font-mono">isTest=true</code>.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Event type</Label>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {(catalog?.categories ?? []).map((cat) => (
                <div key={cat.category}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground px-2 py-1">
                    {cat.category}
                  </div>
                  {cat.events.map((e) => (
                    <SelectItem
                      key={e.key}
                      value={e.key}
                      className="text-xs font-mono"
                    >
                      {e.key}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(eventType)}
            disabled={submitting}
          >
            {submitting ? 'Firing…' : 'Send test event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  W1b-06 -- API Metadata section (WIRED)
// ═══════════════════════════════════════════════════════════════════════════
//
// Sources:
//   - OpenAPI export: GET /api/docs/json (served by fastify-swagger)
//   - CORS allowlist: tenant-config namespace `api.cors`
//                     read via GET /admin/settings/general (locale + ops),
//                     write via POST /admin/settings/tenant-config

interface CorsState {
  origins: string[];
  rateLimitTier: string | null;
}

function MetadataSection() {
  const [origins, setOrigins] = useState<string[]>([]);
  const [rateLimitTier, setRateLimitTier] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newOrigin, setNewOrigin] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiClient.get<{
        namespace: string;
        entries: Record<
          string,
          { value: unknown; changedBy: string | null; changedAt: string; changeReason: string | null }
        >;
      }>('/admin/settings/tenant-config/api');
      const entries = r.entries ?? {};
      const o = entries.cors_origins?.value;
      setOrigins(Array.isArray(o) ? (o as string[]) : []);
      const tier = entries.rate_limit_tier?.value;
      setRateLimitTier(typeof tier === 'string' ? tier : null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load metadata';
      // 404 here is OK -- means namespace not yet seeded.
      if ((err as { status?: number }).status !== 404) {
        toast.error(msg);
      }
      setOrigins([]);
      setRateLimitTier(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function saveOrigins(next: string[]) {
    try {
      setSaving(true);
      await apiClient.patch('/admin/settings/tenant-config/api', {
        batch: { cors_origins: next },
        reason: 'CORS origins updated from admin UI',
      });
      toast.success('CORS origins saved');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function addOrigin() {
    if (!newOrigin.trim()) return;
    if (!/^https?:\/\//.test(newOrigin.trim())) {
      toast.error('Origin must start with http:// or https://');
      return;
    }
    if (origins.includes(newOrigin.trim())) {
      toast.error('Origin already in list');
      return;
    }
    const next = [...origins, newOrigin.trim()];
    setNewOrigin('');
    await saveOrigins(next);
  }

  async function removeOrigin(o: string) {
    const next = origins.filter((x) => x !== o);
    await saveOrigins(next);
  }

  function downloadOpenApi() {
    // OpenAPI spec is served by fastify-swagger at /api/docs/json.
    const url = '/api/docs/json';
    const a = document.createElement('a');
    a.href = url;
    a.download = 'argusai-openapi.json';
    a.click();
    toast.success('OpenAPI spec downloaded (argusai-openapi.json)');
  }

  return (
    <div className="space-y-4">
      <SectionCard
        title="OpenAPI Specification"
        description="Machine-readable description of the ArgusAI API."
        icon={Code}
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={downloadOpenApi}
            title="Download the OpenAPI 3.0.3 spec"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download spec
          </Button>
        }
      >
        <div className="rounded-md border bg-muted/20 p-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <InfoRow label="Current API version" value={<code className="text-[11px]">v1</code>} />
            {/* Pod-QQ — Fixes #648: pre-fix the UI advertised "OpenAPI 3.1"
                but `@fastify/swagger` v9 emits 3.0.3 by default and the
                project does not opt into 3.1.0. The mismatch broke tooling
                that branches on the version string. Now the UI reports the
                actual served version. */}
            <InfoRow label="Spec format" value="OpenAPI 3.0.3" />
            <InfoRow
              label="Spec endpoint"
              value={
                <code className="text-[10px]">
                  {window.location.origin}/api/docs/json
                </code>
              }
            />
            <InfoRow
              label="Interactive docs"
              value={
                <a
                  href="/api/docs"
                  target="_blank"
                  rel="noopener"
                  className="text-primary hover:underline text-[11px]"
                >
                  /api/docs (Swagger UI)
                </a>
              }
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Rate Limit Tiers"
        description="Default rate limits for API consumers. Per-key overrides available on each API key."
        icon={Activity}
      >
        <div className="rounded-md border overflow-hidden">
          <div className="grid grid-cols-[minmax(140px,1fr)_100px_100px_120px] gap-2 px-3 py-2 bg-muted/50 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            <span>Scope</span>
            <span>Rate</span>
            <span>Burst</span>
            <span>Window</span>
          </div>
          <div className="divide-y text-xs">
            {[
              ['Anonymous',    '10/min',   '20',    '1 minute'],
              ['Per API key',  '500/min',  '1000',  '1 minute'],
              ['Per user',     '60/min',   '120',   '1 minute'],
              ['Per tenant',   '5000/min', '10000', '1 minute'],
            ].map(([scope, rate, burst, window]) => (
              <div
                key={scope}
                className="grid grid-cols-[minmax(140px,1fr)_100px_100px_120px] gap-2 px-3 py-2"
              >
                <span className="font-medium">{scope}</span>
                <span className="tabular-nums font-mono">{rate}</span>
                <span className="tabular-nums text-muted-foreground">{burst}</span>
                <span className="text-muted-foreground">{window}</span>
              </div>
            ))}
          </div>
        </div>
        {rateLimitTier && (
          <p className="text-[11px] text-muted-foreground mt-2">
            Tenant rate-limit tier:{' '}
            <code className="font-mono">{rateLimitTier}</code>
          </p>
        )}
      </SectionCard>

      <SectionCard
        title="CORS Origins"
        description="Allowed origins for browser-based API access. Tenant-scoped, persisted in the tenant-config namespace."
        icon={Shield}
      >
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading origins...
          </div>
        ) : (
          <div className="space-y-2">
            {origins.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic py-2">
                No CORS origins configured. The default is to deny all
                browser-origin API calls.
              </p>
            )}
            {origins.map((origin) => (
              <div
                key={origin}
                className="flex items-center justify-between rounded-md border p-2"
              >
                <code className="text-[11px] font-mono">{origin}</code>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => removeOrigin(origin)}
                  disabled={saving}
                  title={`Remove "${origin}"`}
                  aria-label={`Remove origin ${origin}`}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <Input
                value={newOrigin}
                onChange={(e) => setNewOrigin(e.target.value)}
                placeholder="https://app.example.com"
                className="h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addOrigin();
                }}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={addOrigin}
                disabled={saving || !newOrigin.trim()}
                title="Add origin"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">
        {label}
      </div>
      <div>{value}</div>
    </div>
  );
}
